import {
  AirplaneTicketTwoTone,
  ChevronRightTwoTone,
  FindInPageTwoTone,
  FlagTwoTone,
  HomeTwoTone,
  HotelTwoTone,
  KeyboardArrowDownTwoTone,
  KeyboardArrowRightTwoTone,
  KeyboardArrowUpTwoTone,
  MonitorHeartTwoTone,
  PersonTwoTone,
  ReceiptLongTwoTone,
  VerifiedTwoTone,
  WalletTwoTone,
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Link as MLink,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  getGuideSalesIndividualTourDocumentInfo,
  getGuideSalesIndividualTourPaymentInfo,
  getGuideSalesIndividualTourVisaInfo,
  guideSalesIndividualTourGroupInfo,
  guideTourAccommodationInfo,
  guideTourCustomerCount,
  onlineTourDetailPage,
} from '../api';
import { AgeGroupIcon } from '../components/AgeGroupIcon';
import { OnlineTourCard } from '../components/OnlineTourCard';
import { OperationIssues } from '../components/OperationIssues';
import { AuthContext } from '../context/AuthContext';
import {
  IGuideSalesIndividualTourAccommodationInfo,
  IGuideSalesIndividualTourGroupInfo,
  IOnlineTourSearchResult,
  ITourCustomerCount,
  ITourDocumentInfo,
  ITourPaymentInfo,
  ITourVisaInfo,
} from '../utils/types';
import TourPlan from '../components/TourPlan';
import OnlineTourDetailPresentations from '../components/OnlineTourDetailPresentations';
import PageHeader from '../components/PageHeader';

type GuideContainerProps = {
  salesIndividualId: string;
};

export function GuideContainer({ salesIndividualId }: GuideContainerProps) {
  const [tour, setTour] = useState<IOnlineTourSearchResult>();
  const [expandedGroups, setExpandedGroups] = useState<number[]>([]);
  const [searchParams] = useSearchParams();
  const tourId = searchParams.get('tourId');

  const [tourCustomerCount, setTourCustomerCount] =
    useState<ITourCustomerCount>({ capacity: 0, fulfilledCapacity: 0 });
  const [tourGroupInfo, setTourGroupInfo] = useState<
    IGuideSalesIndividualTourGroupInfo[]
  >([]);
  const [accommodationInfo, setAccommodationInfo] = useState<
    IGuideSalesIndividualTourAccommodationInfo[]
  >([]);
  const [tourDocumentInfo, setTourDocumentInfo] =
    useState<ITourDocumentInfo[]>();
  const [tourPaymentInfo, setTourPaymentInfo] = useState<ITourPaymentInfo>();
  const [tourVisaInfo, setTourVisaInfo] = useState<ITourVisaInfo>();
  const { token } = useContext(AuthContext);
  const { t } = useTranslation();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [tourPresentationCollapsed, setTourPresentationCollapsed] =
    useState(true);
  const [tourActivityCollapsed, setTourActivityCollapsed] = useState(true);
  const [hasActivityData, setHasActivityData] = useState<boolean>(true);
  const [hasPresentationData, setHasPresentationData] = useState<boolean>(true);

  useEffect(() => {
    if (tourId) fetchTour(tourId);
    fetchTourCustomerCount(salesIndividualId);
    fetchGuideSalesIndividualTourGroupInfo(salesIndividualId);
    fetchGuideTourAccommodationInfo(salesIndividualId);
    fetchGuideSalesIndividualTourDocumentInfo(salesIndividualId);
    fetchGuideSalesIndividualTourPaymentInfo(salesIndividualId);
    fetchGuideSalesIndividualTourVisaInfo(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  async function fetchTour(tourId: string) {
    const res = await onlineTourDetailPage(parseInt(tourId));
    const data: IOnlineTourSearchResult[] = await res.json();
    if (data.length > 0) {
      setTour(data[0]);
    }
  }

  const fetchTourCustomerCount = async (id: string) => {
    try {
      const res = await guideTourCustomerCount(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setTourCustomerCount(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    }
  };

  const fetchGuideSalesIndividualTourGroupInfo = async (id: string) => {
    try {
      const res = await guideSalesIndividualTourGroupInfo(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setTourGroupInfo(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchGuideTourAccommodationInfo = async (id: string) => {
    try {
      const data = await guideTourAccommodationInfo(id, token);
      setAccommodationInfo(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.message || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchGuideSalesIndividualTourDocumentInfo = async (id: string) => {
    try {
      const data = await getGuideSalesIndividualTourDocumentInfo(id, token);
      setTourDocumentInfo(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };
  const fetchGuideSalesIndividualTourPaymentInfo = async (id: string) => {
    try {
      const data = await getGuideSalesIndividualTourPaymentInfo(id, token);
      setTourPaymentInfo(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };
  const fetchGuideSalesIndividualTourVisaInfo = async (id: string) => {
    try {
      const data = await getGuideSalesIndividualTourVisaInfo(id, token);
      setTourVisaInfo(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress color="secondary" />;
  }

  if (!tourId) {
    return <Navigate to="/account/guidance" />;
  }
  const healthIsExist = false;

  return (
    <Box sx={{ maxWidth: 'lg' }}>
      <PageHeader title={t('guideContainer.tourDetail')} />
      <Breadcrumbs sx={{ mb: 1 }}>
        <MLink
          component={Link}
          to="/"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </MLink>
        <MLink component={Link} to="/account">
          {t('nav.account')}
        </MLink>
        <MLink to="/account/guidance" component={Link}>
          <Typography>{t('nav.guidance')}</Typography>
        </MLink>
        <Typography>{t('guideContainer.tour', { tourId })}</Typography>
      </Breadcrumbs>
      {tour && (
        <OnlineTourCard
          tourId={tour.id}
          tourName={tour.name}
          tourCode={tour.code}
          start={{
            arrivalCity: tour.arrivalArrivalCity,
            departureCity: tour.departureArrivalCity,
            arrivalAirport: tour.arrivalArrival,
            departureAirport: tour.departureArrival,
          }}
          end={{
            arrivalCity: tour.arrivalArrivalCity,
            departureCity: tour.departureArrivalCity,
            arrivalAirport: tour.arrivalArrival,
            departureAirport: tour.departureArrival,
          }}
          tourStartDate={tour.departureDate}
          tourFinishDate={tour.arrivalDate}
          night={tour.dayCount - 1}
          day={tour.dayCount}
          currencyCode={tour.currencyCode}
          remainingTime={tour.remainingDay}
          remainingSeat={
            tourCustomerCount.capacity - tourCustomerCount.fulfilledCapacity
          }
          coverImg={tour.images[0]?.path}
          capacity={tourCustomerCount.capacity}
          disableActions
          hideShareAndPrint
        />
      )}

      <Typography variant="h6" sx={{ mt: 4 }}>
        {t('guideContainer.groups')}
      </Typography>
      <Paper sx={{ maxWidth: 'sm', mt: 2 }}>
        <List>
          {tourGroupInfo.map((group, i) => (
            <Box key={i}>
              <ListItemButton
                onClick={() => {
                  setExpandedGroups((prev) =>
                    prev.includes(group.tourGroupId)
                      ? prev.filter((id) => id !== group.tourGroupId)
                      : [...prev, group.tourGroupId],
                  );
                }}
              >
                <ListItemIcon>
                  <FlagTwoTone />
                </ListItemIcon>
                <ListItemText
                  primary={group.tourGroupName}
                  secondary={group.guideInfo.map((g) => (
                    <>
                      {g.fullName} - {g.position} <br />
                    </>
                  ))}
                />
                <Typography sx={{ mr: 2 }}>
                  {' '}
                  {t('guideContainer.totalCustomers', {
                    count: group.groupInfo.reduce(
                      (acc, curr) => acc + curr.settled,
                      0,
                    ),
                  })}
                </Typography>
                {expandedGroups.includes(group.tourGroupId) ? (
                  <KeyboardArrowUpTwoTone />
                ) : (
                  <KeyboardArrowDownTwoTone />
                )}
              </ListItemButton>
              {expandedGroups.includes(group.tourGroupId) && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    m: 2,
                    py: 1,
                    px: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ fontSize: '1rem', mb: 1 }}
                    >
                      {t('guideContainer.totalCustomers', {
                        count: group.groupInfo.reduce(
                          (acc, curr) => acc + curr.settled,
                          0,
                        ),
                      })}
                    </Typography>
                  </Box>
                  <Grid2 container spacing={1} justifyContent="center">
                    {group.groupInfo.map((g, i) => (
                      <Grid2 key={g.ageGroupName + i}>
                        <Chip
                          label={
                            <>
                              <Typography variant="inherit">
                                {g.ageGroupName}
                              </Typography>
                              <Typography
                                variant="inherit"
                                sx={{ textAlign: 'center' }}
                              >
                                {g.settled}
                              </Typography>
                            </>
                          }
                          icon={
                            <AgeGroupIcon
                              startMonths={g.startAgeInMonth}
                              gender={g.gender}
                            />
                          }
                          size="medium"
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>
              )}
              {i !== tourGroupInfo.length - 1 && <Divider />}
            </Box>
          ))}
        </List>
      </Paper>

      <Typography variant="h6" sx={{ mt: 4 }}>
        {t('guideContainer.operations')}
      </Typography>
      <Paper sx={{ maxWidth: 'sm', mt: 2 }}>
        <List>
          <ListItemButton
            component={Link}
            to={`/account/guidance/${salesIndividualId}/customers?tourId=${tourId}`}
          >
            <ListItemIcon>
              <PersonTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.customers')} />
            <ChevronRightTwoTone />
          </ListItemButton>
          <ListItemButton
            component={Link}
            to={`/account/guidance/${salesIndividualId}/accommodation?tourId=${tourId}`}
          >
            <ListItemIcon>
              <HotelTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.accommodation')} />
            <Stack direction="row" spacing={1}>
              <OperationIssues
                count={accommodationInfo.reduce(
                  (acc, curr) => acc + curr.unSettledPax,
                  0,
                )}
              />
              <ChevronRightTwoTone />
            </Stack>
          </ListItemButton>
          <ListItemButton
            component={Link}
            to={`/account/guidance/${salesIndividualId}/transportation?tourId=${tourId}`}
          >
            <ListItemIcon>
              <AirplaneTicketTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.transportation')} />
            <ChevronRightTwoTone />
          </ListItemButton>
          <ListItemButton>
            <ListItemIcon>
              <FindInPageTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.documents')} />
            <Stack direction="row" spacing={1}>
              <OperationIssues
                count={
                  tourDocumentInfo?.reduce(
                    (acc, curr) =>
                      curr.requiredDocumentType.code !== 'PST'
                        ? acc + curr.requiredCount - curr.count
                        : acc,
                    0,
                  ) || 0
                }
              />
              <ChevronRightTwoTone />
            </Stack>
          </ListItemButton>
          <ListItemButton
            component={Link}
            to={`/account/guidance/${salesIndividualId}/passport?tourId=${tourId}`}
          >
            <ListItemIcon>
              <WalletTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.passport')} />
            <Stack direction="row" spacing={1}>
              <OperationIssues
                count={
                  tourDocumentInfo?.reduce(
                    (acc, curr) =>
                      curr.requiredDocumentType.code === 'PST'
                        ? acc + curr.requiredCount - curr.count
                        : acc,
                    0,
                  ) || 0
                }
              />
              <ChevronRightTwoTone />
            </Stack>
          </ListItemButton>
          <ListItemButton
            component={Link}
            to={`/account/guidance/${salesIndividualId}/visa?tourId=${tourId}`}
          >
            <ListItemIcon>
              <VerifiedTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.visa')} />
            <Stack direction="row" spacing={1}>
              <OperationIssues
                count={
                  (tourVisaInfo?.requiredCount || 0) -
                  (tourVisaInfo?.completedCount || 0)
                }
              />
              <ChevronRightTwoTone />
            </Stack>
          </ListItemButton>
          <ListItemButton
            component={Link}
            to={`/account/guidance/${salesIndividualId}/payment?tourId=${tourId}`}
          >
            <ListItemIcon>
              <ReceiptLongTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.payment')} />
            <Stack direction="row" spacing={1}>
              <OperationIssues
                count={
                  (tourPaymentInfo?.notPaymentCount || 0) +
                  (tourPaymentInfo?.notFullAmountPaymentCount || 0)
                }
              />
              <ChevronRightTwoTone />
            </Stack>
          </ListItemButton>
          <ListItemButton
            component={Link}
            to={`/account/guidance/${salesIndividualId}/health?tourId=${tourId}`}
            disabled={healthIsExist}
          >
            <ListItemIcon>
              <MonitorHeartTwoTone />
            </ListItemIcon>
            <ListItemText primary={t('guideContainer.health')} />
            <Stack direction="row" spacing={1}>
              <ChevronRightTwoTone />
            </Stack>
          </ListItemButton>
        </List>
      </Paper>
      {!hasActivityData && !hasPresentationData && (
        <Typography sx={{ mt: 2 }}>
          {t('onlineTourDetail.noNameYet')}
        </Typography>
      )}
      {hasPresentationData && (
        <>
          <Collapse
            collapsedSize={200}
            in={!tourPresentationCollapsed}
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                mt: 3,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: tourPresentationCollapsed ? 100 : 0,
                  backgroundImage: 'linear-gradient(transparent, white)',
                  pointerEvents: 'none',
                  display: 'block',
                },
              }}
            >
              <OnlineTourDetailPresentations
                tourId={parseInt(tourId)}
                onEmptyDetails={(hasData) => setHasPresentationData(hasData)}
              />
            </Box>
          </Collapse>
          <Button
            onClick={() =>
              setTourPresentationCollapsed(!tourPresentationCollapsed)
            }
            sx={{ mt: 2 }}
          >
            {tourPresentationCollapsed
              ? t('orderDetailPage.showMoreDetails')
              : t('orderDetailPage.showLessDetails')}
            {tourPresentationCollapsed ? (
              <KeyboardArrowRightTwoTone />
            ) : (
              <KeyboardArrowUpTwoTone />
            )}
          </Button>
        </>
      )}
      {hasActivityData && (
        <>
          <Collapse
            collapsedSize={300}
            in={!tourActivityCollapsed}
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                mt: 3,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: tourActivityCollapsed ? 100 : 0,
                  backgroundImage: 'linear-gradient(transparent, white)',
                  pointerEvents: 'none',
                  display: 'block',
                },
              }}
            >
              <TourPlan
                salesIndividualId={parseInt(salesIndividualId)}
                onEmptyDetails={(hasData) => setHasActivityData(hasData)}
              />
            </Box>
          </Collapse>
          <Button
            onClick={() => setTourActivityCollapsed(!tourActivityCollapsed)}
            sx={{ mt: 2 }}
          >
            {tourActivityCollapsed
              ? t('orderDetailPage.showMoreDetails')
              : t('orderDetailPage.showLessDetails')}
            {tourActivityCollapsed ? (
              <KeyboardArrowRightTwoTone />
            ) : (
              <KeyboardArrowUpTwoTone />
            )}
          </Button>
        </>
      )}
    </Box>
  );
}
